import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  Skeleton,
  IconButton,
  Tooltip,
  TablePagination,
  alpha,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  PriorityHigh as HighPriorityIcon,
  LowPriority as LowPriorityIcon,
  SwapHoriz as NormalPriorityIcon,
  Person,
} from '@mui/icons-material';
import { CobrancaFinanceiro } from '../../../types';
import { CobrancaFinanceiroService } from '../../../services/CobrancaFinanceiroService';
import { formatarValor, formatarData, formatarCPFCNPJ } from '../../../utils/formatToView';
import { useRelatorioCores } from '../../relatorios';

interface CobrancaTableProps {
  responsavel_id?: number;
  onManage?: (cobranca: CobrancaFinanceiro) => void;
}

const COL_COUNT = 7;

const statusMap: Record<string, { label: string; colorKey: 'neutral' | 'aguardando' | 'bloqueado' | 'ativo' | 'cancelado' | 'secundario' | 'primario' }> = {
  pendente: { label: 'Pendente', colorKey: 'neutral' },
  em_contato: { label: 'Em Contato', colorKey: 'aguardando' },
  promessa_pagamento: { label: 'Promessa Pgto', colorKey: 'bloqueado' },
  pagou: { label: 'Pagou', colorKey: 'ativo' },
  sem_retorno: { label: 'Sem Retorno', colorKey: 'cancelado' },
  negociacao: { label: 'Negociação', colorKey: 'secundario' },
  cancelado: { label: 'Cancelado', colorKey: 'cancelado' },
  finalizado: { label: 'Finalizado', colorKey: 'ativo' },
};

const priorityMap: Record<string, { label: string; colorKey: 'cancelado' | 'primario' | 'ativo'; icon: React.ReactNode }> = {
  alta: { label: 'Alta', colorKey: 'cancelado', icon: <HighPriorityIcon sx={{ fontSize: 14 }} /> },
  normal: { label: 'Normal', colorKey: 'primario', icon: <NormalPriorityIcon sx={{ fontSize: 14 }} /> },
  baixa: { label: 'Baixa', colorKey: 'ativo', icon: <LowPriorityIcon sx={{ fontSize: 14 }} /> },
};

function chipColor(
  key: 'neutral' | 'aguardando' | 'bloqueado' | 'ativo' | 'cancelado' | 'secundario' | 'primario',
  cores: ReturnType<typeof useRelatorioCores>,
): string {
  switch (key) {
    case 'neutral':
      return '#9e9e9e';
    case 'aguardando':
      return cores.aguardando;
    case 'bloqueado':
      return cores.bloqueado;
    case 'ativo':
      return cores.ativo;
    case 'cancelado':
      return cores.cancelado;
    case 'secundario':
      return cores.secundario;
    case 'primario':
    default:
      return cores.primario;
  }
}

function RelatorioStyleChip({
  label,
  colorHex,
  icon,
}: {
  label: string;
  colorHex: string;
  icon?: React.ReactNode;
}) {
  return (
    <Chip
      icon={icon as any}
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(colorHex, 0.12),
        color: colorHex,
        fontWeight: 700,
        fontSize: '0.68rem',
        height: 22,
        border: `1px solid ${alpha(colorHex, 0.3)}`,
        '& .MuiChip-icon': { color: `${colorHex} !important` },
      }}
    />
  );
}

const CobrancaTable: React.FC<CobrancaTableProps> = ({ responsavel_id, onManage }) => {
  const navigate = useNavigate();
  const cores = useRelatorioCores();
  const [cobrancas, setCobrancas] = useState<CobrancaFinanceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchCobrancas = async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = {};
      if (responsavel_id) params.responsavel_id = responsavel_id;

      const data = await CobrancaFinanceiroService.getAll(params);
      setCobrancas(data || []);
    } catch (error) {
      console.error('Erro ao buscar cobranças:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCobrancas();
  }, [responsavel_id]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginated = cobrancas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" aria-label="tabela de cobranças">
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => `color-mix(in srgb, ${cores.primario} 12%, ${theme.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(cores.primario, 0.2)}`,
                },
              }}
            >
              <TableCell>Cliente</TableCell>
              <TableCell>Data Cobrança</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: COL_COUNT }).map((__, j) => (
                    <TableCell key={j} sx={{ py: 0.5 }}>
                      <Skeleton variant="text" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : cobrancas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.disabled">
                    Nenhuma cobrança encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((cobranca) => {
                const st = statusMap[cobranca.status];
                const pr = priorityMap[cobranca.prioridade];
                const statusColor = st ? chipColor(st.colorKey, cores) : '#9e9e9e';
                const prioColor = pr ? chipColor(pr.colorKey, cores) : cores.primario;

                return (
                  <TableRow
                    key={cobranca.id}
                    hover
                    sx={{ '&:hover': { bgcolor: alpha(cores.primario, 0.03) } }}
                  >
                    <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '0.78rem',
                              cursor: cobranca.cliente_id ? 'pointer' : 'default',
                              '&:hover': cobranca.cliente_id ? {
                                color: 'primary.main',
                                textDecoration: 'underline',
                              } : {},
                              transition: 'color 0.2s',
                            }}
                            onClick={() => cobranca.cliente_id && navigate(`/clients/${cobranca.cliente_id}`)}
                          >
                            {cobranca.cliente?.nome || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                            {formatarCPFCNPJ(cobranca.cliente?.cpf_cnpj || '') || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarData(cobranca.data_cobranca)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>
                      {formatarValor(cobranca.valor_original)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {pr ? (
                        <RelatorioStyleChip label={pr.label} colorHex={prioColor} icon={pr.icon} />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {st ? (
                        <RelatorioStyleChip label={st.label} colorHex={statusColor} />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{cobranca.status}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {cobranca.responsavel?.nome || 'Não atribuído'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.25}>
                        <Tooltip title="Gerenciar Negociação">
                          <IconButton size="small" color="primary" onClick={() => onManage?.(cobranca)} sx={{ p: 0.5 }}>
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && cobrancas.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.8,
            px: 2,
            bgcolor: alpha(cores.primario, 0.04),
            borderTop: `1px solid ${alpha(cores.primario, 0.15)}`,
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={cobrancas.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por pág:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              width: '100%',
              '.MuiTablePagination-toolbar': {
                minHeight: 32,
                flexWrap: 'nowrap',
                justifyContent: 'center',
                gap: 1,
                p: 0,
                '& .MuiTablePagination-spacer': { display: 'none' },
              },
              '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
              '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
              '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
              '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default CobrancaTable;
